/**
 * LES CONFIRMATIONS À CRÉER, CÔTÉ ÉCRAN — lot 147, 02/11/2026.
 *
 * Demande de Mohamed : « il faut un espace où on peut mettre toutes les
 * confirmations à créer afin de pouvoir travailler à côté et y revenir tout
 * vérifier d'un coup ». Le serveur est au lot 146
 * (`apps/api/src/confirmations_a_creer.ts`).
 *
 * TROIS PARTIES DANS CE FICHIER :
 *   1. les appels à l'API et au rangement ;
 *   2. des fonctions PURES, que l'écran affiche et que les tests tiennent ;
 *   3. LA LECTURE EN ARRIÈRE-PLAN.
 *
 * ---------------------------------------------------------------------------
 * POURQUOI LA LECTURE VIT ICI ET NON DANS L'ÉCRAN DES ORDRES
 * ---------------------------------------------------------------------------
 * L'écran des ordres est DÉMONTÉ dès qu'on va sur le planning ou les journées
 * (`Application.tsx`, le choix de l'écran). Une lecture qu'il porterait
 * s'arrêterait avec lui — le contraire de « travailler à côté ». Elle vit donc
 * au niveau du module, lancée par l'application à l'ouverture et par l'écran
 * après un dépôt : elle continue tant que Stafa est ouvert, quel que soit
 * l'écran. Fermé, les confirmations restent « à lire » et la lecture reprend à
 * la réouverture — réponse de Mohamed, 02/11.
 */

import { appeler, echec, type Resultat } from './api.js';
import { enBase64, type LectureFaite, type TransportPropose } from './confirmation_lue.js';
import { adresseDuFichier, envoyerLeFichier } from './rangement.js';

// ---------------------------------------------------------------------------
// 1. Ce que l'API rend, et les appels
// ---------------------------------------------------------------------------

export type EtatConfirmation = 'en_envoi' | 'a_lire' | 'lue' | 'echec' | 'creee' | 'ecartee';

export interface ConfirmationACreer {
  readonly id: string;
  readonly nom: string;
  readonly chemin: string;
  readonly etat: EtatConfirmation;
  readonly message: string | null;
  readonly rang_transport: number;
  readonly transports_du_document: number | null;
  readonly proposition: TransportPropose | null;
  readonly remarques: readonly string[] | null;
  readonly modele: string | null;
  readonly depose_par_nom: string | null;
  readonly cree_le: string;
  /** L'ordre qui porte déjà la référence lue — signalé, pas écarté. */
  readonly doublon: { readonly ot_id: string; readonly numero: string } | null;
}

export async function listerLesConfirmations(
  jeton: string,
): Promise<Resultat<readonly ConfirmationACreer[]>> {
  const echange = await appeler('/confirmations-a-creer', jeton);
  if (echange.etat !== 'succes') return echec(echange);
  const corps = echange.corps as { confirmations?: ConfirmationACreer[] };
  return { etat: 'ok', valeur: corps.confirmations ?? [] };
}

/**
 * DÉPOSER UN FICHIER : la ligne, le fichier, puis « envoyée ». Même ordre que
 * les pièces jointes : si le fichier ne part pas, la ligne reste « en envoi »
 * et se voit dans la liste, où on peut l'écarter.
 */
export async function deposerUneConfirmation(
  jeton: string,
  fichier: File,
): Promise<Resultat<null>> {
  const declare = await appeler('/confirmations-a-creer', jeton, {
    methode: 'POST',
    corps: { nom: fichier.name, poids: fichier.size },
  });
  if (declare.etat !== 'succes') return echec(declare);
  const ligne = (declare.corps as { confirmation?: ConfirmationACreer }).confirmation;
  if (ligne === undefined) return { etat: 'echec', cause: 'injoignable' };

  const envoye = await envoyerLeFichier(jeton, ligne.chemin, fichier);
  if (envoye.etat !== 'ok') return { etat: 'echec', cause: 'injoignable' };

  const confirme = await appeler(
    `/confirmations-a-creer/${encodeURIComponent(ligne.id)}/envoyee`,
    jeton,
    { methode: 'POST' },
  );
  if (confirme.etat !== 'succes') return echec(confirme);
  return { etat: 'ok', valeur: null };
}

/** Ce que donne la lecture d'UNE ligne. */
export type IssueDeLecture = 'lue' | 'occupee' | 'panne';

/**
 * LIRE UNE LIGNE : relire le fichier dans le rangement, l'envoyer au serveur,
 * qui lit et enregistre. `occupee` : un autre onglet la lit déjà (409).
 * `panne` : le fichier ou le serveur n'a pas répondu — on s'arrête là, la
 * ligne reste à lire.
 *
 * LE FICHIER EST RELU PAR UNE ADRESSE SIGNÉE, le chemin déjà éprouvé par
 * l'onglet Documents, plutôt que par un autre appel au rangement.
 */
export async function lireUneDeposee(jeton: string, id: string, chemin: string): Promise<IssueDeLecture> {
  const adresse = await adresseDuFichier(jeton, chemin);
  if (adresse.etat !== 'ok') return 'panne';
  let octets: ArrayBuffer;
  try {
    const reponse = await fetch(adresse.valeur);
    if (!reponse.ok) return 'panne';
    octets = await reponse.arrayBuffer();
  } catch {
    return 'panne';
  }
  const echange = await appeler(`/confirmations-a-creer/${encodeURIComponent(id)}/lire`, jeton, {
    methode: 'POST',
    corps: { document: enBase64(octets) },
  });
  if (echange.etat === 'succes') return 'lue';
  if (echange.etat === 'refus' && echange.statut === 409) return 'occupee';
  return 'panne';
}

export async function marquerCreee(jeton: string, id: string, otId: string): Promise<Resultat<null>> {
  const echange = await appeler(`/confirmations-a-creer/${encodeURIComponent(id)}/creee`, jeton, {
    methode: 'POST',
    corps: { ot_id: otId },
  });
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: null };
}

export async function ecarterLaConfirmation(jeton: string, id: string): Promise<Resultat<null>> {
  const echange = await appeler(`/confirmations-a-creer/${encodeURIComponent(id)}/ecartee`, jeton, {
    methode: 'POST',
  });
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: null };
}

/**
 * RELIRE UN ÉCHEC : la ligne est relue tout de suite, par ce même onglet.
 * Geste volontaire — la lecture en arrière-plan ne relit jamais un échec,
 * chaque lecture coûte.
 */
export function relire(jeton: string, c: ConfirmationACreer): Promise<IssueDeLecture> {
  return lireUneDeposee(jeton, c.id, c.chemin);
}

// ---------------------------------------------------------------------------
// 2. Les fonctions pures
// ---------------------------------------------------------------------------

/** Combien attendent d'être vérifiées — le chiffre de l'onglet. */
export function nombreAVerifier(liste: readonly ConfirmationACreer[]): number {
  return liste.filter((c) => c.etat === 'lue').length;
}

/** La prochaine à vérifier, en sautant celle qu'on vient de traiter. */
export function suivanteAVerifier(
  liste: readonly ConfirmationACreer[],
  sauf: string | null = null,
): ConfirmationACreer | undefined {
  return liste.find((c) => c.etat === 'lue' && c.id !== sauf);
}

/** La prochaine à lire, en sautant celles qu'un autre onglet lit déjà. */
export function prochaineALire(
  liste: readonly ConfirmationACreer[],
  sautees: ReadonlySet<string>,
): ConfirmationACreer | undefined {
  return liste.find((c) => c.etat === 'a_lire' && !sautees.has(c.id));
}

/**
 * UNE LIGNE LUE, MISE SOUS LA FORME D'UNE LECTURE UNE PAR UNE — pour passer
 * par `suiteDeLaLecture` et ouvrir la saisie exactement comme le lot 124. UN
 * seul transport : les autres du document ont leur propre ligne.
 */
export function lectureDe(c: ConfirmationACreer): LectureFaite | null {
  if (c.etat !== 'lue' || c.proposition === null) return null;
  return {
    lu: true,
    transports: [c.proposition],
    remarques: c.remarques ?? [],
    modele: c.modele ?? '',
  };
}

function lieu(e: { site_code_postal: string | null; site_ville: string | null; site_nom: string }): string {
  const morceaux = [e.site_code_postal, e.site_ville].filter((m): m is string => m !== null && m !== '');
  return morceaux.length > 0 ? morceaux.join(' ') : e.site_nom;
}

/** « 93290 TREMBLAY → 28140 POUPRY » : du premier chargement à la dernière livraison. */
export function trajetDe(p: TransportPropose): string {
  const depart = p.etapes.find((e) => e.type === 'chargement');
  const arrivees = p.etapes.filter((e) => e.type === 'livraison');
  const arrivee = arrivees[arrivees.length - 1];
  if (depart === undefined && arrivee === undefined) return '—';
  return `${depart === undefined ? '?' : lieu(depart)} → ${arrivee === undefined ? '?' : lieu(arrivee)}`;
}

/** Le jour du premier chargement, sinon de la première étape datée. */
export function jourDe(p: TransportPropose): string | null {
  const charge = p.etapes.find((e) => e.type === 'chargement' && e.jour !== null);
  return charge?.jour ?? p.etapes.find((e) => e.jour !== null)?.jour ?? null;
}

/** Le mot de l'état, pour la pastille. */
export function etatDit(etat: EtatConfirmation): string {
  switch (etat) {
    case 'en_envoi':
      return 'Envoi';
    case 'a_lire':
      return 'Lecture…';
    case 'lue':
      return 'À vérifier';
    case 'echec':
      return 'Échec';
    case 'creee':
      return 'Créée';
    case 'ecartee':
      return 'Écartée';
  }
}

// ---------------------------------------------------------------------------
// 3. La lecture en arrière-plan
// ---------------------------------------------------------------------------

/** Ce dont la boucle a besoin — passé en argument pour être éprouvé sans réseau. */
export interface OutilsDeLecture {
  readonly jeton: () => Promise<string | null>;
  readonly lister: (jeton: string) => Promise<readonly ConfirmationACreer[] | null>;
  readonly lire: (jeton: string, c: ConfirmationACreer) => Promise<IssueDeLecture>;
  /** Appelé après chaque lecture, pour que l'écran se rafraîchisse. */
  readonly quandChange: () => void;
}

/** Au plus autant de lectures d'une traite : une borne contre une boucle folle. */
export const LECTURES_AU_PLUS = 100;

/**
 * LIRE TANT QU'IL Y A À LIRE, UNE À LA FOIS.
 *
 * UNE À LA FOIS, et non toutes ensemble : dix appels de trente secondes en
 * parallèle tiendraient dix connexions à la base ouvertes, et un refus de
 * l'API pour excès d'appels ferait tout échouer d'un coup.
 *
 * ELLE S'ARRÊTE À LA PREMIÈRE PANNE : sans réseau, insister ne ferait que
 * tourner. Les lignes restent « à lire » et la prochaine ouverture reprend.
 *
 * Rend le nombre de lignes lues.
 */
export async function lireTantQuIlYEnA(o: OutilsDeLecture): Promise<number> {
  const sautees = new Set<string>();
  let lues = 0;
  for (let tour = 0; tour < LECTURES_AU_PLUS; tour += 1) {
    const jeton = await o.jeton();
    if (jeton === null) return lues;
    const liste = await o.lister(jeton);
    if (liste === null) return lues;
    const suivante = prochaineALire(liste, sautees);
    if (suivante === undefined) return lues;
    const issue = await o.lire(jeton, suivante);
    if (issue === 'panne') return lues;
    if (issue === 'occupee') {
      sautees.add(suivante.id);
      continue;
    }
    lues += 1;
    o.quandChange();
  }
  return lues;
}

const abonnes = new Set<() => void>();
let enCours: Promise<number> | null = null;
/**
 * UNE DEMANDE ARRIVÉE PENDANT QU'UNE BOUCLE TOURNE. La boucle a peut-être
 * déjà fait son dernier tour de liste : sans ce drapeau, le fichier déposé à
 * cet instant attendrait la prochaine ouverture de Stafa.
 */
let relancer = false;

/** L'écran s'abonne pour se rafraîchir après chaque lecture. Rend le désabonnement. */
export function sAbonnerALaLecture(quandChange: () => void): () => void {
  abonnes.add(quandChange);
  return () => {
    abonnes.delete(quandChange);
  };
}

/**
 * LANCER LA LECTURE, si elle ne tourne pas déjà. Appelée par l'application
 * quand on est connecté, et par l'écran après un dépôt. Deux appels de suite
 * ne lancent qu'une boucle.
 */
export function lancerLaLecture(jeton: () => Promise<string | null>): void {
  if (enCours !== null) {
    relancer = true;
    return;
  }
  relancer = false;
  enCours = lireTantQuIlYEnA({
    jeton,
    lister: async (j) => {
      const r = await listerLesConfirmations(j);
      return r.etat === 'ok' ? r.valeur : null;
    },
    lire: (j, c) => lireUneDeposee(j, c.id, c.chemin),
    quandChange: () => {
      for (const f of abonnes) f();
    },
  }).finally(() => {
    enCours = null;
    for (const f of abonnes) f();
    if (relancer) lancerLaLecture(jeton);
  });
}
